/* eslint-disable @typescript-eslint/no-explicit-any */
import { api_service } from '../api/api-service';
import { api_urls } from '../api/api-urls';

export interface BankAccountApiResponse {
    Id: string;
    accountType?: string;
    AccountType?: string;
    BankName: string;
    IdentificationNumber: string;
    AccountNumber: string;
    AspireRecordId?: string;
    AspireEntityOId?: string;
    IsActive: boolean;
    AspireIsActive?: boolean;
    NickName?: string;
    nickName?: string;
    accountNickname?: string;
    AutoPay?: boolean;
    Country?: string;
}

export interface ApiResponse {
    statusCode: number;
    data: BankAccountApiResponse[];
    message?: string;
}

export interface CreateBankAccountPayload {
    bankName: string;
    nickName?: string | null;
    accountNumber: string;
    country: string;
    accountType: string;
    routingNumber: string;
}

export interface CreateBankAccountResponse {
    statusCode: number;
    data?: any;
    message?: string;
}

export const getBankAccountsList = async (showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: api_urls.GetBankAccounts,
            showLoader,
        });
        return response?.data as ApiResponse;
    } catch (error: any) {
        console.error('Error fetching bank accounts:', error);
        throw error;
    }
};

export const createBankAccount = async (
    payload: CreateBankAccountPayload,
    showLoader: boolean = true,
): Promise<CreateBankAccountResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.CreateBankAccounts,
            payLoad: payload,
            showLoader,
        });
        return response?.data as CreateBankAccountResponse;
    } catch (error: any) {
        console.error('Error creating bank account:', error);
        throw error;
    }
};

interface EncryptedAccountNumberResponse {
    statusCode: number;
    data: {
        id: string;
        accountNumber?: string;
        key: string;
        iv: string;
        cipherText: string;
    };
    message?: string;
}

const base64ToBytes = (value: string): Uint8Array<ArrayBuffer> =>
    Uint8Array.from(atob(value), char => char.charCodeAt(0));

/**
 * Decrypt a value encrypted by the backend with a one-time AES-256-GCM key.
 * cipherText is the ciphertext followed by the 16-byte auth tag (Web Crypto format).
 */
const decryptWithOneTimeKey = async (key: string, iv: string, cipherText: string): Promise<string> => {
    const cryptoKey = await window.crypto.subtle.importKey('raw', base64ToBytes(key), { name: 'AES-GCM' }, false, [
        'decrypt',
    ]);
    const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: base64ToBytes(iv) },
        cryptoKey,
        base64ToBytes(cipherText),
    );
    return new TextDecoder().decode(decrypted);
};

/**
 * Get the full account number of one bank account, for the unmask action on the bank account card.
 * Do not store the returned value; call this each time the user unmasks.
 */
export const getUnmaskedAccountNumber = async (id: string, showLoader: boolean = false): Promise<string> => {
    try {
        const response: any = await api_service.get({
            endPoint: `${api_urls.GetBankAccountNumber}/${id}/account-number`,
            showLoader,
        });
        const data = (response?.data as EncryptedAccountNumberResponse).data;
        // Some environments return the plain number instead of the encrypted payload.
        if (typeof data.accountNumber === 'string' && data.accountNumber) return data.accountNumber;
        return await decryptWithOneTimeKey(data.key, data.iv, data.cipherText);
    } catch (error: any) {
        console.error('Error unmasking account number:', error);
        throw error;
    }
};

/**
 * Get the full routing number of one bank account, for the unmask action on the bank account card.
 * It comes from the same endpoint as the account number; `data.routingNumber` is either an
 * encrypted `{ key, iv, cipherText }` payload (decrypted like the account number) or plain text.
 * Do not store the returned value; call this each time the user unmasks.
 */
export const getUnmaskedRoutingNumber = async (id: string, showLoader: boolean = false): Promise<string> => {
    try {
        const response: any = await api_service.get({
            endPoint: `${api_urls.GetBankAccountNumber}/${id}/account-number`,
            showLoader,
        });
        const routing = response?.data?.data?.routingNumber;
        if (typeof routing === 'string' && routing) return routing;
        if (routing?.key && routing?.iv && routing?.cipherText) {
            return await decryptWithOneTimeKey(routing.key, routing.iv, routing.cipherText);
        }
        throw new Error('Routing number not available in response');
    } catch (error: any) {
        console.error('Error unmasking routing number:', error);
        throw error;
    }
};

export const updateBankAccountStatus = async (
    id: string,
    isActive: boolean,
    showLoader: boolean = true,
): Promise<CreateBankAccountResponse> => {
    try {
        const response: any = await api_service.patch({
            endPoint: `${api_urls.UpdateBankAccountStatus}/${id}/status`,
            payLoad: { isActive },
            showLoader,
        });
        return response?.data as CreateBankAccountResponse;
    } catch (error: any) {
        console.error('Error updating bank account status:', error);
        throw error;
    }
};
